import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { FileText, ExternalLink, Package, Folder, Home, TrendingUp } from 'lucide-react'
import { seoAnalytics } from '@/lib/api'

interface ConversionsByLandingTableProps {
  days: number
  section?: string
}

const URL_TYPE_ICONS: Record<string, any> = {
  product: Package,
  category: Folder,
  home: Home,
  cms: FileText,
  other: FileText,
}

const URL_TYPE_LABELS: Record<string, string> = {
  product: 'Producto',
  category: 'Categoria',
  home: 'Inicio',
  cms: 'CMS',
  other: 'Otro',
}

export default function ConversionsByLandingTable({ days, section }: ConversionsByLandingTableProps) {
  const [view, setView] = useState<'landing' | 'product'>('landing')

  const landingQuery = useQuery({
    queryKey: ['seo-conversions-landing', days, section],
    queryFn: () => seoAnalytics.getConversionsByLanding(days, 50, section),
    enabled: view === 'landing',
  })

  const productQuery = useQuery({
    queryKey: ['seo-conversions-product', days, section],
    queryFn: () => seoAnalytics.getConversionsByProduct(days, 50, section),
    enabled: view === 'product',
  })

  const isLoading = view === 'landing' ? landingQuery.isLoading : productQuery.isLoading
  const data = view === 'landing' ? landingQuery.data?.data : productQuery.data?.data
  const items = data || []

  // Truncate URL for display
  const truncateUrl = (url: string, maxLength: number = 50) => {
    if (!url) return '-'
    if (url.length <= maxLength) return url
    return url.substring(0, maxLength) + '...'
  }

  return (
    <div className="card">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/30">
            <TrendingUp className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-900 dark:text-white">Conversiones por Landing</h3>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Paginas con mejor tasa de conversion
            </p>
          </div>
        </div>

        {/* View toggle */}
        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          <button
            onClick={() => setView('landing')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              view === 'landing'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Todas
          </button>
          <button
            onClick={() => setView('product')}
            className={`px-3 py-1 text-sm rounded-md transition-colors ${
              view === 'product'
                ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
            }`}
          >
            Productos
          </button>
        </div>
      </div>

      <div className="card-body p-0">
        {isLoading ? (
          <div className="p-8 text-center">
            <div className="animate-pulse text-slate-400">Cargando datos...</div>
          </div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-slate-400">
            <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
            <p>No hay datos de conversiones</p>
            <p className="text-sm mt-1">Sincroniza las conversiones para ver datos</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-700">
                  <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    {view === 'product' ? 'Producto' : 'Landing Page'}
                  </th>
                  {view === 'landing' && (
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                      Tipo
                    </th>
                  )}
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Sesiones
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Conversiones
                  </th>
                  <th className="text-right py-3 px-4 text-xs font-medium text-slate-500 uppercase tracking-wider">
                    Tasa
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {items.slice(0, 20).map((item: any, idx: number) => {
                  const TypeIcon = URL_TYPE_ICONS[item.landing_type] || FileText
                  return (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        {view === 'product' ? (
                          <div>
                            <span className="text-sm font-medium text-slate-900 dark:text-white">
                              ID: {item.product_id}
                            </span>
                          </div>
                        ) : (
                          <a
                            href={`https://www.jvs-informatica.com${item.landing_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-slate-700 dark:text-slate-300 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1"
                            title={item.landing_url}
                          >
                            {truncateUrl(item.landing_url)}
                            <ExternalLink className="w-3 h-3 opacity-50" />
                          </a>
                        )}
                      </td>
                      {view === 'landing' && (
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1.5">
                            <TypeIcon className="w-4 h-4 text-slate-400" />
                            <span className="text-sm text-slate-600 dark:text-slate-400">
                              {URL_TYPE_LABELS[item.landing_type] || item.landing_type}
                            </span>
                          </div>
                        </td>
                      )}
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm text-slate-600 dark:text-slate-400">
                          {item.sessions?.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className="text-sm font-medium text-slate-900 dark:text-white">
                          {item.conversions?.toLocaleString()}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <span className={`text-sm font-medium ${
                          item.conversion_rate > 2
                            ? 'text-green-600 dark:text-green-400'
                            : item.conversion_rate > 0.5
                            ? 'text-amber-600 dark:text-amber-400'
                            : 'text-slate-600 dark:text-slate-400'
                        }`}>
                          {item.conversion_rate}%
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* Show count */}
        {items.length > 0 && (
          <div className="px-4 py-3 border-t border-slate-200 dark:border-slate-700 text-sm text-slate-500">
            Mostrando {Math.min(20, items.length)} de {items.length} {view === 'product' ? 'productos' : 'paginas'}
            <span className="ml-2 text-xs">(min 5-10 sesiones)</span>
          </div>
        )}
      </div>
    </div>
  )
}
